/**
 * Cognito Authentication Service
 *
 * Wraps amazon-cognito-identity-js with async/await.
 * The SDK is callback-based — each operation is wrapped in a Promise.
 *
 * Requires VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID
 * environment variables. When env vars are missing (e.g. in tests),
 * all operations return gracefully without throwing.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession
} from 'amazon-cognito-identity-js';

const SERVICE_TAG = '[COGNITO]';

const USER_POOL_ID = (import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '').trim();
const CLIENT_ID = (import.meta.env.VITE_COGNITO_CLIENT_ID ?? '').trim();

if (!USER_POOL_ID || !CLIENT_ID) {
  console.warn(
    '[CognitoService] VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_CLIENT_ID is not set. Auth will not work.'
  );
}

/** Lazily-initialized user pool — avoids constructor throw when env vars are empty */
let _userPool: CognitoUserPool | null = null;

/**
 * Get or create the CognitoUserPool singleton.
 * Returns null if env vars are not configured or the constructor throws
 * (e.g. invalid UserPoolId format).
 */
function getUserPool(): CognitoUserPool | null {
  if (!USER_POOL_ID || !CLIENT_ID) {
    return null;
  }
  if (!_userPool) {
    try {
      _userPool = new CognitoUserPool({
        UserPoolId: USER_POOL_ID,
        ClientId: CLIENT_ID
      });
    } catch (err) {
      console.error(
        `[CognitoService] Failed to create UserPool — check VITE_COGNITO_USER_POOL_ID format (expected: region_XXXXXXXXX, got length ${USER_POOL_ID.length})`,
        err
      );
      return null;
    }
  }
  return _userPool;
}

/** Decoded session data returned from sign-in and rehydration */
export interface CognitoSessionResult {
  sub: string;
  username: string;
  email: string;
  idToken: string;
}

/**
 * Extract user info from a Cognito session's ID token payload.
 *
 * @param session - A valid CognitoUserSession
 * @returns Decoded session data
 */
function extractSessionData(session: CognitoUserSession): CognitoSessionResult {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    sub: payload['sub'] as string,
    username: (payload['custom:username'] as string) ?? (payload['cognito:username'] as string) ?? '',
    email: payload['email'] as string,
    idToken: idToken.getJwtToken()
  };
}

export const cognitoService = {
  /**
   * Register a new user with username, email and password.
   *
   * Creates a Cognito user with email as the sign-in alias
   * and custom:username as the display name.
   *
   * @param username - Display name (stored as custom:username)
   * @param email - Email address (used as sign-in identifier)
   * @param password - Password (min 8 characters)
   */
  signUp(username: string, email: string, password: string): Promise<void> {
    console.info(`${SERVICE_TAG} Signing up`, { username, email });

    const pool = getUserPool();
    if (!pool) {
      return Promise.reject(new Error('Cognito is not configured'));
    }

    return new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'custom:username', Value: username })
      ];

      pool.signUp(email, password, attributes, [], (err) => {
        if (err) {
          console.error(`${SERVICE_TAG} Sign-up failed`, { code: err.name, message: err.message });
          reject(err);
          return;
        }
        console.info(`${SERVICE_TAG} Sign-up successful`, { username });
        resolve();
      });
    });
  },

  /**
   * Sign in with email and password.
   *
   * On success, returns decoded token data including sub, username, email,
   * and the raw ID token string for Authorization headers.
   *
   * @param email - Email address
   * @param password - Password
   * @returns Session data with sub, username, email, and idToken
   */
  signIn(email: string, password: string): Promise<CognitoSessionResult> {
    console.info(`${SERVICE_TAG} Signing in`, { email });

    const pool = getUserPool();
    if (!pool) {
      return Promise.reject(new Error('Cognito is not configured'));
    }

    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: pool
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const data = extractSessionData(session);
          console.info(`${SERVICE_TAG} Sign-in successful`, { sub: data.sub, username: data.username });
          resolve(data);
        },
        onFailure: (err) => {
          console.error(`${SERVICE_TAG} Sign-in failed`, { code: err.name, message: err.message });
          reject(err);
        }
      });
    });
  },

  /**
   * Sign out the current user.
   *
   * Performs a local sign-out (clears tokens from storage).
   */
  signOut(): void {
    const pool = getUserPool();
    if (!pool) return;

    const currentUser = pool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
      console.info(`${SERVICE_TAG} Signed out`);
    }
  },

  /**
   * Get the current authenticated session (for token refresh / rehydration).
   *
   * Returns session data if a valid (non-expired) session exists,
   * or null if no current user or the session is expired.
   *
   * @returns Session data or null
   */
  getCurrentSession(): Promise<CognitoSessionResult | null> {
    const pool = getUserPool();
    if (!pool) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const currentUser = pool.getCurrentUser();
      if (!currentUser) {
        resolve(null);
        return;
      }

      currentUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            console.info(`${SERVICE_TAG} No valid session found`);
            resolve(null);
            return;
          }

          const data = extractSessionData(session);
          console.info(`${SERVICE_TAG} Session rehydrated`, { sub: data.sub, username: data.username });
          resolve(data);
        }
      );
    });
  },

  /**
   * Get the current ID token string for Authorization headers.
   *
   * Convenience wrapper around getCurrentSession() that returns
   * just the token string, or null if not authenticated.
   *
   * @returns JWT ID token string or null
   */
  async getIdToken(): Promise<string | null> {
    const session = await cognitoService.getCurrentSession();
    return session?.idToken ?? null;
  },

  /**
   * Initiate the forgot password flow.
   *
   * Sends a verification code to the user's email address.
   *
   * @param email - Email address of the account
   */
  forgotPassword(email: string): Promise<void> {
    console.info(`${SERVICE_TAG} Initiating forgot password`, { email });

    const pool = getUserPool();
    if (!pool) {
      return Promise.reject(new Error('Cognito is not configured'));
    }

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: pool
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          console.info(`${SERVICE_TAG} Forgot password code sent`, { email });
          resolve();
        },
        onFailure: (err) => {
          console.error(`${SERVICE_TAG} Forgot password failed`, { code: err.name, message: err.message });
          reject(err);
        }
      });
    });
  },

  /**
   * Change password for the currently authenticated user.
   *
   * Requires a valid session — the user must be signed in.
   *
   * @param oldPassword - Current password
   * @param newPassword - New password to set
   */
  changePassword(oldPassword: string, newPassword: string): Promise<void> {
    console.info(`${SERVICE_TAG} Changing password`);

    const pool = getUserPool();
    if (!pool) {
      return Promise.reject(new Error('Cognito is not configured'));
    }

    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      return Promise.reject(new Error('No authenticated user'));
    }

    return new Promise((resolve, reject) => {
      currentUser.getSession((err: Error | null) => {
        if (err) {
          reject(err);
          return;
        }
        currentUser.changePassword(oldPassword, newPassword, (err2) => {
          if (err2) {
            console.error(`${SERVICE_TAG} Change password failed`, { code: err2.name, message: err2.message });
            reject(err2);
            return;
          }
          console.info(`${SERVICE_TAG} Password changed successfully`);
          resolve();
        });
      });
    });
  },

  /**
   * Confirm forgot password with verification code and new password.
   *
   * Completes the password reset flow after the user receives
   * the verification code via email.
   *
   * @param email - Email address of the account
   * @param code - Verification code from email
   * @param newPassword - New password to set
   */
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    console.info(`${SERVICE_TAG} Confirming forgot password`, { email });

    const pool = getUserPool();
    if (!pool) {
      return Promise.reject(new Error('Cognito is not configured'));
    }

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: pool
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          console.info(`${SERVICE_TAG} Password reset successful`, { email });
          resolve();
        },
        onFailure: (err) => {
          console.error(`${SERVICE_TAG} Password reset failed`, { code: err.name, message: err.message });
          reject(err);
        }
      });
    });
  }
};
